import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Loader2, CheckCircle, Lock } from 'lucide-react'
import { Button, ErrorBanner } from '@/components'
import { authService } from '@/services/authService'
import { useLanguage } from '@/contexts/LanguageContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'

const PasswordResetScreen: React.FC = () => {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email.trim()) {
      setError(t.auth.passwordReset.emailRequired)
      return
    }

    if (!isValidEmail(email)) {
      setError(t.auth.passwordReset.emailInvalid)
      return
    }

    if (isLoading) return

    setIsLoading(true)
    setError(null)

    try {
      // Backend handles both password reset and email sending
      const resetResponse = await authService.requestPasswordReset(email)
      
      if (!resetResponse.success) {
        // Backend'in mesajı korunur; yoksa bağlamsal yedek metin kullanılır.
        setError(responseErrorMessage(resetResponse, t, t.auth.passwordReset.error))
        return
      }

      // Backend already sent the email, just show success
        setIsSuccess(true)
      
    } catch (error) {
      // Ham error.message kullanıcıya dökülmesin: ağ/oturum/sunucu ayrımı
      // toUserMessage içinde yapılıp yerelleştirilmiş metne çevriliyor.
      logError('PasswordResetScreen.handleSubmit', error)
      setError(toUserMessage(error, t, t.auth.passwordReset.error))
    } finally {
      setIsLoading(false)
    }
  }

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 max-w-md w-full"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t.auth.passwordReset.successTitle}</h2>
            <p className="text-gray-600 mb-6">
              <strong>{email}</strong> {t.auth.passwordReset.successMessage}
              <br /><br />
              <span className="text-orange-600 font-medium">
                {t.auth.passwordReset.warningMessage}
              </span>
            </p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white">
                  {t.auth.passwordReset.backToLoginButton}
                </Button>
              </Link>
              <button
                onClick={() => {
                  setIsSuccess(false)
                  setEmail('')
                }}
                className="w-full text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                {t.auth.passwordReset.resendButton}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 max-w-md w-full"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg border border-gray-100">
            <Lock className="w-8 h-8 text-gray-600" />
          </div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            {t.auth.passwordReset.title}
          </h1>
          <p className="text-gray-600 text-sm">
            {t.auth.passwordReset.description}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t.auth.passwordReset.emailLabel}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError(null)
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder={t.auth.passwordReset.emailPlaceholder}
                required
              />
            </div>
          </div>

          {/* Error Message */}
          <ErrorBanner message={error} onDismiss={() => setError(null)} />

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t.auth.passwordReset.sending}</span>
              </div>
            ) : (
              t.auth.passwordReset.sendButton
            )}
          </Button>
        </form>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t.auth.passwordReset.backToLogin}</span>
          </Link>
        </div>
      </motion.div>
    </div>
  )
}

export default PasswordResetScreen 